import thresh from '@/lib/genThresh';
import cToF from 'celsius-to-fahrenheit';

export default {
  title: 'Introduction',
  img: 'intro',
  subsections: [{
      title: 'General Information',
      content: [{
        tag: 'p',
        content: `This API is intended to be a standalone access point for data produced by Breezi AirPulse devices installed throughout
        commercial properties, but can also be used as a companion resource for the Breezi-Pro Progressive Web App (PWA).
        By utilizing queries adherent to the documentation found here, you will have flexible, efficient access to measurements,
        both contemporary and historical, representing the conditions throughout the requested property. The data returned from this
        API will always be in JSON format. Optional parameters exist for some services that will allow the response to be more easily
        parsed into other formats, such as .csv. For an example, please see the <b>?alerts</b> Query Param Parameter portion of the Measurements
        Service section in this documentation. Likewise, any service which requires the user to send a body, such as the POST request to
        the <b>Auth</b> service, must also be in JSON format.`
      }]
    },
    {
      title: 'Services Overview',
      content: [{
          tag: 'p',
          content: 'The REST services accessible with this API are as follows:'
        },
        {
          tag: 'ul',
          content: [
            'Account Service',
            'AirPulse Service',
            'Measurements Service',
            'Alerts Service',
            'Snapshot Service'
          ]
        }
      ]
    },
    {
      title: 'Measurement Values',
      content: [{
          tag: 'p',
          content: `The <b>Alerts</b> and <b>Measurements</b>
          Services return a wide array of measurements concerning the area around where the requested AirPulse device is installed,
          or Datapoint. Readings which are tracked and returned by the API are as follows:`
        },
        {
          tag: 'ul',
          content: [
            'Temperature (˚C or ˚F)',
            'Relative Humidity (%)',
            'Indoor Air Quality (iaq index)',
            'Ambient eCO2 (parts per million/ppm)',
            'Total Volatile Organic Compounds (tVOCs, ppm)',
            'Device Remaining Battery Life (%)'
          ]
        },
        {
          tag: 'p',
          content: 'In the event that an AirPulse is installed on an HVAC Air Filter, responses can also include:'
        },
        {
          tag: 'ul',
          content: [
            'Filter Condition (days remaining until filter life depleted, percentage of life remaining, average delta pressure recorded throughout the day in question)'
          ]
        }
      ]
    },
    {
      title: 'Default Alert Thresholds',
      content: [{
          tag: 'p',
          content: `The crux of BreeziPro Commercial API's capability as an effective monitoring tool for the conditions and health of
          your properties is its comparsion of values against a set of alert thresholds, also called Setpoints. Custom Setpoints can be
          set in the BreeziPro PWA, and the next version of the API will come equipped with the ability to update these thresholds
          programmatically through a simple REST call. In order to set a precedent for quality monitoring of alert conditions, a default
          set of Setpoints is applied automatically to the readings produced by your AirPulse devices. These values are set based on
          industry standards for Air Quality, Temperature and other conditions. In the table below, please note the listed default
          thresholds, as well as the <b>Abbreviation</b> values, which will serve as identifiers for the ?include and ?omit parameters for
          each reading in the <b>Services Section</b> of this documentation.`
        },
        {
          tag: 'thresh',
          content: formatThresh()
        }
      ]
    }
  ]
}

function formatThresh() {
  const props = ['label', 'term', 'rdg', 'min', 'max', 'good', 'bad', 'crit', 'bad2', 'crit2'];
  return Object.keys(thresh).map(rdg => {
    return props.map(p => {
      const t = thresh[rdg][p];
      if (['string', 'number'].includes(typeof t)) {
        if (rdg != 'ttp') {
          return typeof t == 'string' ? t.toString() : t.toString() + thresh[rdg].term;
        } else {
          return typeof t == 'number' ?
            `${thresh[rdg][p]}˚C/${cToF(thresh[rdg][p])}˚F` :
            (p == 'term' ? '˚C/˚F' : t);
        }
      } else {
        try {
          return rdg != 'ttp' ?
            `${t.min}${thresh[rdg].term} ~ ${t.max}${thresh[rdg].term}` :
            `${t.min}˚C/${Math.round(cToF(t.min))}˚F ~ ${t.max}˚C/${Math.round(cToF(t.max))}˚F`;
        } catch (e) {
          return '-';
        }
      }
    });
  });
}

function shrthnd(str) {
  return !['hhm', 'ttp'].includes(str) ? str : (str == 'hhm' ? 'hum' : 'temp')
}



// WEBPACK FOOTER //
// ./src/lib/apiDocTemplates/introduction.js